import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useUnsavedChanges } from "@/lib/unsavedChanges";
import { ArrowLeft, RefreshCw, Trash2, UserPlus, Pause, Play, Eye } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TenantContractFields } from "@/components/TenantContractFields";
import { TenantContractHistory } from "@/components/TenantContractHistory";
import { PasswordVisibilityField } from "@/components/PasswordVisibilityField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function toIsoDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 10); }
  catch { return ""; }
}
function fromIsoDate(d) {
  if (!d) return null;
  return new Date(d + "T00:00:00Z").toISOString();
}

export default function SuperAdminTenant() {
  const { id } = useParams();
  const nav = useNavigate();
  const { startImpersonation } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [form, setForm] = useState(null);
  const [baseForm, setBaseForm] = useState(null);
  const [baseSub, setBaseSub] = useState(null);
  const [subForm, setSubForm] = useState({ subscription_start: "", subscription_end: "", status: "active", payment_notes: "" });
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", role: "tenant_admin" });
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/tenants/${id}`);
      const t = r.data;
      setTenant(t);
      setUsers(t.users || []);
      setForm({
        name: t.name || "", company_name: t.company_name || "",
        vat_number: t.vat_number || "", tax_code: t.tax_code || "",
        sdi_code: t.sdi_code || "", pec_email: t.pec_email || "",
        address: t.address || "", cap: t.cap || "", city: t.city || "", province: t.province || "",
        administrator_full_name: t.administrator_full_name || "",
        country: t.country || "IT", phone: t.phone || "", email: t.email || "",
        notes: t.notes || "", plan: t.plan || "standard",
        max_products: t.max_products ?? "", max_brands: t.max_brands ?? "",
        agreed_price: t.agreed_price ?? "",
      });
      setBaseForm(JSON.stringify({
        name: t.name || "", company_name: t.company_name || "",
        vat_number: t.vat_number || "", tax_code: t.tax_code || "",
        sdi_code: t.sdi_code || "", pec_email: t.pec_email || "",
        address: t.address || "", cap: t.cap || "", city: t.city || "", province: t.province || "",
        administrator_full_name: t.administrator_full_name || "",
        country: t.country || "IT", phone: t.phone || "", email: t.email || "",
        notes: t.notes || "", plan: t.plan || "standard",
        max_products: t.max_products ?? "", max_brands: t.max_brands ?? "",
        agreed_price: t.agreed_price ?? "",
      }));
      const sub = {
        subscription_start: toIsoDate(t.subscription_start),
        subscription_end: toIsoDate(t.subscription_end),
        status: t.status || "active",
        payment_notes: t.payment_notes || "",
      };
      setSubForm(sub);
      setBaseSub(JSON.stringify(sub));
    } catch (e) {
      toast.error("Errore caricamento abbonamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const saveAnagrafica = async () => {
    setSaving(true);
    try {
      if (!form.email.trim()) { toast.error("Email aziendale obbligatoria"); return false; }
      const toIntOrNull = (v) => (v === "" || v === null ? null : parseInt(v, 10));
      const toFloatOrNull = (v) => (v === "" || v === null ? null : parseFloat(String(v).replace(",", ".")));
      const originalEmail = JSON.parse(baseForm).email || "";
      const emailChanged = form.email.trim().toLowerCase() !== originalEmail.trim().toLowerCase();
      const payload = {
        ...form,
        max_products: toIntOrNull(form.max_products),
        max_brands: toIntOrNull(form.max_brands),
        agreed_price: toFloatOrNull(form.agreed_price),
      };
      if (emailChanged) {
        payload.email = form.email.trim().toLowerCase();
        payload.expected_email = originalEmail;
      } else delete payload.email;
      await axios.put(`${API}/tenants/${id}`, payload);
      toast.success(emailChanged ? "Email aziendale e accesso admin aggiornati" : "Anagrafica salvata");
      await load();
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : Array.isArray(detail)
        ? detail.map((item) => item.msg).join(", ") : detail?.message || "Errore salvataggio");
      return false;
    }
    finally { setSaving(false); }
  };

  const saveSubscription = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/tenants/${id}/subscription`, {
        subscription_start: fromIsoDate(subForm.subscription_start),
        subscription_end: fromIsoDate(subForm.subscription_end),
        status: subForm.status,
        payment_notes: subForm.payment_notes,
      });
      toast.success("Abbonamento aggiornato");
      await load();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); return false; }
    finally { setSaving(false); }
  };

  // Unsaved-changes guard for both the tenant anagrafica and subscription forms.
  const formDirty = !!form && baseForm !== null && baseForm !== JSON.stringify(form);
  const subDirty = baseSub !== null && baseSub !== JSON.stringify(subForm);
  useUnsavedChanges(formDirty || subDirty, async () => {
    if (formDirty && !(await saveAnagrafica())) return false;
    if (subDirty && !(await saveSubscription())) return false;
    return true;
  });

  const impersonate = async () => {
    if (formDirty || subDirty) {
      toast.error("Salva le modifiche prima di entrare nella vista cliente.");
      return;
    }
    setImpersonating(true);
    try {
      await startImpersonation(id);
      nav("/products");
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Impossibile entrare nella vista cliente.");
    } finally { setImpersonating(false); }
  };

  const renew = async () => {
    if (renewing) return;
    if (subDirty || formDirty) { toast.error("Salva prima le modifiche all'anagrafica e all'abbonamento."); return; }
    if (!confirm("Rinnovare l'abbonamento di 1 anno? Il primo admin dovrà firmare di nuovo il contratto; le firme precedenti resteranno archiviate.")) return;
    setRenewing(true);
    try {
      await axios.post(`${API}/tenants/${id}/renew?years=1&renewal_id=${crypto.randomUUID()}`);
      toast.success("Abbonamento rinnovato: nuova firma richiesta al primo admin.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore rinnovo"); }
    finally { setRenewing(false); }
  };

  const toggleSuspend = async () => {
    if (tenant.access_state === "expired" && tenant.status !== "suspended") return;
    const newStatus = tenant.status === "active" ? "suspended" : "active";
    if (!confirm(`Confermi di ${newStatus === "suspended" ? "sospendere" : "riattivare"} l'abbonamento?`)) return;
    try {
      await axios.put(`${API}/tenants/${id}/subscription`, { status: newStatus });
      toast.success("Stato aggiornato");
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tenants/${id}/users`, {
        ...newUser, email: users.length === 0 ? form.email : newUser.email,
      });
      toast.success("Utente creato");
      setShowNewUser(false);
      setNewUser({ email: "", password: "", role: "tenant_admin" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Errore"); }
  };

  const changeUserRole = async (uid, role) => {
    try {
      await axios.put(`${API}/tenants/${id}/users/${uid}`, { role });
      toast.success("Ruolo aggiornato");
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const resetUserPassword = async (uid) => {
    const p = prompt("Nuova password (min 6 caratteri)");
    if (!p || p.length < 6) return;
    try {
      await axios.put(`${API}/tenants/${id}/users/${uid}`, { password: p });
      toast.success("Password temporanea impostata: l'utente dovrà cambiarla al prossimo accesso.");
      await load();
    } catch (e) { toast.error("Errore"); }
  };

  const deleteUser = async (uid) => {
    if (!confirm("Eliminare l'utente?")) return;
    try {
      await axios.delete(`${API}/tenants/${id}/users/${uid}`);
      toast.success("Utente eliminato");
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const deleteTenant = async () => {
    if (deleteConfirm !== "ELIMINA") return;
    try {
      await axios.delete(`${API}/tenants/${id}`);
      toast.success("Abbonamento eliminato");
      nav("/admin");
    } catch (e) { toast.error("Errore"); }
  };

  if (loading || !tenant) return <div className="p-10 text-sm text-[#8A8579]">Caricamento...</div>;

  return (
    <div className="p-8 md:p-12 max-w-5xl" data-testid="superadmin-tenant-page">
      <Link to="/admin" className="inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-[#8A8579] hover:text-[#1A1A1A]">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Torna agli abbonamenti
      </Link>
      <div className="mt-6 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A8579]">Abbonamento</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mt-2">{tenant.name}</h1>
          <div className="mt-2 flex items-center gap-3 text-sm">
            <span className={`inline-block px-2.5 py-1 text-[9px] tracking-[0.24em] uppercase rounded-full ${
              tenant.access_state === "active" ? "bg-[#E9F0EA] text-[#4A6B53]" :
              tenant.access_state === "suspended" ? "bg-[#FBF1E4] text-[#8A6A2F]" : "bg-[#F5E4E4] text-[#B24040]"
            }`} data-testid="tenant-status-badge">{{ active: "Attivo", suspended: "Sospeso", expired: "Scaduto" }[tenant.access_state]}</span>
            <span className="text-[#8A8579]">{tenant.stats?.products || 0} prodotti · {tenant.stats?.brands || 0} brand · {tenant.stats?.clients || 0} clienti</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={impersonate} disabled={impersonating || saving}
            data-testid="impersonate-tenant-button">
            <Eye className="w-4 h-4 mr-2" strokeWidth={1.5}/>{impersonating ? "Accesso..." : "Impersona · sola lettura"}
          </Button>
          <Button variant="outline" onClick={renew} disabled={renewing || saving} data-testid="renew-btn"><RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.5}/>{renewing ? "Rinnovo..." : "Rinnova 1 anno"}</Button>
          {tenant.access_state !== "expired" && <Button variant="outline" onClick={toggleSuspend} data-testid="suspend-btn">
            {tenant.status === "active" ? <><Pause className="w-4 h-4 mr-2" strokeWidth={1.5}/>Sospendi</> : <><Play className="w-4 h-4 mr-2" strokeWidth={1.5}/>Riattiva</>}
          </Button>}
          <Button variant="outline" onClick={()=>setShowDelete(true)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-tenant-btn">
            <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5}/>Elimina
          </Button>
        </div>
      </div>

      {tenant.access_state !== "active" && <p className="mt-5 border-l-2 border-[#A94736] bg-[#FAF1ED] px-4 py-3 text-sm text-[#672F27]" data-testid="tenant-restriction-info">
        {tenant.access_state === "expired" ? "Scaduto: rinnova l’abbonamento per riattivare servizi e link pubblici." : "Sospeso manualmente: rinnova se necessario, poi seleziona Riattiva. I dati restano consultabili."}
      </p>}

      {/* Anagrafica fatturazione */}
      <section className="mt-10 bg-white border border-[#E5E5E0] rounded-sm p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-xl text-[#1A1A1A]">Anagrafica fatturazione</h2>
          <Button size="sm" onClick={saveAnagrafica} disabled={saving} className="bg-[#1A1A1A] text-white hover:bg-black" data-testid="save-anagrafica-btn">
            {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label className="text-xs uppercase tracking-widest text-[#8A8579]">Nome commerciale</Label>
            <Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} /></div>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TenantContractFields values={form} prefix="tenant-contract" required={false}
              onChange={(key, value)=>setForm(prev=>({...prev, [key]:value}))} />
          </div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8A8579]">SDI</Label>
            <Input value={form.sdi_code} onChange={(e)=>setForm({...form, sdi_code:e.target.value})} /></div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8A8579]">Paese</Label>
            <Input value={form.country} onChange={(e)=>setForm({...form, country:e.target.value})} /></div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8A8579]">Telefono</Label>
            <Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} /></div>
          <div className="md:col-span-2"><Label htmlFor="tenant-admin-company-email" className="text-xs uppercase tracking-widest text-[#8A8579]">Email aziendale e admin</Label>
            <Input id="tenant-admin-company-email" type="email" value={form.email}
              onChange={(e)=>setForm({...form, email:e.target.value})} required autoComplete="off"
              data-testid="tenant-admin-company-email" />
            {form.email.trim().toLowerCase() !== (tenant.email || "").trim().toLowerCase() &&
              <p className="text-xs text-[#8C3A3A] mt-2" data-testid="tenant-admin-email-change-notice">
                Cambierà anche l’email di accesso del primo admin: la password resta la stessa, ma le sessioni attuali dovranno accedere di nuovo. Comunica il nuovo indirizzo al cliente.
              </p>}
          </div>
          <div className="md:col-span-2"><Label className="text-xs uppercase tracking-widest text-[#8A8579]">Note</Label>
            <Textarea rows={3} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} /></div>
        </div>

        {/* Condizioni commerciali (uso interno) */}
        <div className="mt-8 pt-6 border-t border-[#E5E5E0]">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A8579] mb-4">Condizioni commerciali (uso interno)</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8A8579]">Limite prodotti</Label>
              <Input type="number" min="0" placeholder="Illimitato" value={form.max_products}
                     onChange={(e)=>setForm({...form, max_products:e.target.value})} data-testid="tenant-max-products" />
              <div className="text-[11px] text-[#8A8579] mt-1" data-testid="tenant-products-usage">
                In uso: {tenant.stats?.products ?? 0}{form.max_products ? ` / ${form.max_products}` : " (illimitato)"}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8A8579]">Limite brand</Label>
              <Input type="number" min="0" placeholder="Illimitato" value={form.max_brands}
                     onChange={(e)=>setForm({...form, max_brands:e.target.value})} data-testid="tenant-max-brands" />
              <div className="text-[11px] text-[#8A8579] mt-1" data-testid="tenant-brands-usage">
                In uso: {tenant.stats?.brands ?? 0}{form.max_brands ? ` / ${form.max_brands}` : " (illimitato)"}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8A8579]">Prezzo pattuito (€ / anno, no IVA)</Label>
              <Input type="number" min="0" step="0.01" placeholder="0,00" value={form.agreed_price}
                     onChange={(e)=>setForm({...form, agreed_price:e.target.value})} data-testid="tenant-agreed-price" />
              <div className="text-[11px] text-[#8A8579] mt-1" data-testid="tenant-agreed-price-disclosure">
                Uso interno; se inserisci il prezzo nel contratto, sarà visibile all’admin cliente.
              </div>
            </div>
          </div>
          <p className="text-[11px] text-[#8A8579] mt-3">Lascia vuoto un limite per renderlo illimitato. Al raggiungimento del limite, il cliente non potrà creare nuovi prodotti/brand.</p>
        </div>
      </section>

      <TenantContractHistory tenantId={id} />

      {/* Abbonamento */}
      <section className="mt-6 bg-white border border-[#E5E5E0] rounded-sm p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-xl text-[#1A1A1A]">Abbonamento</h2>
          <Button size="sm" onClick={saveSubscription} disabled={saving} className="bg-[#1A1A1A] text-white hover:bg-black" data-testid="save-subscription-btn">
            {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label className="text-xs uppercase tracking-widest text-[#8A8579]">Data inizio</Label>
            <Input type="date" value={subForm.subscription_start} onChange={(e)=>setSubForm({...subForm, subscription_start:e.target.value})} data-testid="sub-start"/></div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8A8579]">Data fine</Label>
            <Input type="date" value={subForm.subscription_end} onChange={(e)=>setSubForm({...subForm, subscription_end:e.target.value})} data-testid="sub-end"/></div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8A8579]">Stato</Label>
            <Select value={subForm.status} onValueChange={(v)=>setSubForm({...subForm, status:v})}>
              <SelectTrigger data-testid="sub-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Attivo</SelectItem>
                <SelectItem value="suspended">Sospeso</SelectItem>
                <SelectItem value="expired">Scaduto</SelectItem>
              </SelectContent>
            </Select></div>
          <div className="md:col-span-3"><Label className="text-xs uppercase tracking-widest text-[#8A8579]">Note pagamento</Label>
            <Textarea rows={2} value={subForm.payment_notes} onChange={(e)=>setSubForm({...subForm, payment_notes:e.target.value})} placeholder="Es. Bonifico ricevuto il 12/02/2026 · rif. FT-2026-001" /></div>
        </div>
      </section>

      {/* Utenti */}
      <section className="mt-6 bg-white border border-[#E5E5E0] rounded-sm p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-xl text-[#1A1A1A]">Utenti ({users.length})</h2>
          <Button size="sm" onClick={()=>setShowNewUser(true)} className="bg-[#1A1A1A] text-white hover:bg-black" data-testid="new-user-btn">
            <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />Nuovo utente
          </Button>
        </div>
        {users.length === 0 ? (
          <div className="text-sm text-[#8A8579] py-8 text-center">Nessun utente. Creane uno per permettere al cliente di accedere.</div>
        ) : (
          <div className="border border-[#E5E5E0] rounded-sm overflow-hidden">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-[#F5F2EC]">
                <tr className="text-left text-[10px] tracking-[0.24em] uppercase text-[#8A8579]">
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Ruolo</th>
                  <th className="px-4 py-3">Creato</th>
                  <th className="px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-[#F5F2EC] last:border-0" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 text-[#1A1A1A]">{u.email}
                      {u.id === (tenant.contract_signer_user_id || users.find(item => item.role === "tenant_admin")?.id) &&
                        <span className="ml-2 text-xs text-[#4A6B53]" data-testid={`tenant-contract-signer-${u.id}`}>Firmatario</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Select value={u.role} onValueChange={(v)=>changeUserRole(u.id, v)}
                              disabled={u.id === (tenant.contract_signer_user_id || users.find(item => item.role === "tenant_admin")?.id)}>
                        <SelectTrigger className="w-40 h-8"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tenant_admin">Admin</SelectItem>
                          <SelectItem value="tenant_readonly">Sola lettura</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-[#8A8579] text-[13px]">{u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "-"}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button onClick={()=>resetUserPassword(u.id)} className="text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A] hover:text-[#8A8579] underline" data-testid={`reset-pwd-${u.id}`}>Reset password</button>
                      {u.id !== (tenant.contract_signer_user_id || users.find(item => item.role === "tenant_admin")?.id) &&
                        <button onClick={()=>deleteUser(u.id)} className="text-[10px] tracking-[0.24em] uppercase text-red-600 hover:text-red-800" data-testid={`delete-user-${u.id}`}>Elimina</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Dialogs */}
      <Dialog open={showNewUser} onOpenChange={setShowNewUser}>
        <DialogContent data-testid="new-user-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Nuovo utente</DialogTitle>
            <DialogDescription className="sr-only">Assegna ruolo e password temporanea all'utente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createUser} className="space-y-4 pt-2">
            <div><Label className="text-xs uppercase tracking-widest text-[#8A8579]">Email</Label>
              <Input type="email" value={users.length === 0 ? form.email : newUser.email}
                onChange={(e)=>setNewUser({...newUser, email:e.target.value})}
                disabled={users.length === 0} required data-testid="new-user-email" />
              {users.length === 0 && <p className="text-xs text-[#8A8579] mt-1" data-testid="first-user-company-email-note">
                Il primo admin usa l’email aziendale impostata nell’anagrafica.
              </p>}</div>
            <div><Label className="text-xs uppercase tracking-widest text-[#8A8579]">Password temporanea (min 6)</Label>
              <PasswordVisibilityField value={newUser.password} onChange={(e)=>setNewUser({...newUser, password:e.target.value})}
                required minLength={6} autoComplete="new-password" testId="new-user-password" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-[#8A8579]">Ruolo</Label>
              <Select value={newUser.role} onValueChange={(v)=>setNewUser({...newUser, role:v})}>
                <SelectTrigger data-testid="new-user-role"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tenant_admin">Admin (può modificare tutto)</SelectItem>
                  <SelectItem value="tenant_readonly">Sola lettura (può vedere e scaricare)</SelectItem>
                </SelectContent>
              </Select></div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={()=>setShowNewUser(false)} data-testid="new-user-cancel">Annulla</Button>
              <Button type="submit" className="bg-[#1A1A1A] text-white hover:bg-black" data-testid="new-user-submit">Crea</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent data-testid="delete-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-red-600">Eliminare l'abbonamento?</DialogTitle>
            <DialogDescription className="text-sm">
              Questa azione è <strong>irreversibile</strong>. Verranno cancellati tutti i dati del cliente
              (prodotti, brand, clienti, foto, INCI, layout QR, utenti) e non potranno essere recuperati.
              Per confermare, scrivi la parola <strong>ELIMINA</strong> in maiuscolo:
              <span className="font-mono block mt-2 text-[#1A1A1A]">ELIMINA</span>
            </DialogDescription>
          </DialogHeader>
          <Input value={deleteConfirm} onChange={(e)=>setDeleteConfirm(e.target.value)} placeholder="ELIMINA" data-testid="delete-confirm-input"/>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowDelete(false)}>Annulla</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" disabled={deleteConfirm !== "ELIMINA"} onClick={deleteTenant} data-testid="delete-tenant-confirm">
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
